import {
  TrendingUp, Layers, ClipboardCheck, ShieldCheck, Workflow, BarChart3,
} from "lucide-react";
import { Reveal, Kicker } from "../lib/motion";

const SERVICES = [
  {
    icon: TrendingUp,
    title: "Virtual & Fractional CFO",
    copy: "Senior finance leadership for strategy, growth, decision-making and value creation.",
    items: ["Financial strategy", "Business planning", "Cash-flow management", "FP&A", "Board & investor reporting", "KPI management"],
  },
  {
    icon: Layers,
    title: "Finance Shared Services & Outsourced Finance",
    copy: "Scalable finance shared services and day-to-day accounting operations, from transaction processing through management reporting.",
    items: ["Accounting", "Accounts Payable", "Accounts Receivable", "Reconciliations", "Payroll coordination", "Monthly financial reporting & MIS", "Finance shared services"],
  },
  {
    icon: ClipboardCheck,
    title: "Controllership & Reporting",
    copy: "Close, consolidate and report with accuracy, timeliness and transparency.",
    items: ["Month-end close", "Consolidation", "Multi-entity reporting", "Revenue recognition", "Accounting policies", "Audit readiness"],
  },
  {
    icon: ShieldCheck,
    title: "Compliance & Regulatory",
    copy: "Compliance managed systematically — accurately, consistently and on time.",
    items: ["GST, TDS & income tax", "Payroll compliance", "ROC / Companies Act", "Cross-border withholding", "Overseas filings", "Compliance calendars"],
  },
  {
    icon: Workflow,
    title: "Treasury, Controls & Finance Transformation",
    copy: "Cash, controls, systems and processes designed for scale.",
    items: ["Treasury & working capital", "Internal financial controls", "Process redesign — O2C, P2P, R2R", "Finance systems", "Custom AI tools & workflow automation"],
  },
  {
    icon: BarChart3,
    title: "FP&A & Investor Reporting",
    copy: "Budgets, forecasts, dashboards and investor-ready reporting that improve decision-making.",
    items: ["Budgeting & forecasting", "Scenario planning", "Financial models", "SaaS metrics & unit economics", "Dashboards", "Fundraising support"],
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="bg-white py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal className="max-w-2xl">
        <Kicker>Services</Kicker>
        <h2 className="mt-6 font-display text-4xl md:text-5xl font-bold tracking-tight text-navy">
          How We Help
        </h2>
        <p className="mt-5 text-base md:text-lg leading-relaxed text-body">
          Six integrated service lines — engaged individually, or combined into a
          complete finance function for your business.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.1}>
            <article
              id={i === 1 ? "finance-shared-services" : undefined}
              data-testid={`service-card-${i}`}
              className="group relative h-full rounded-xl border border-border bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(10,31,60,0.10)] before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:rounded-t-xl before:bg-brand before:opacity-0 before:transition-opacity before:duration-300 hover:before:opacity-100"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-navy/[0.05] text-navy transition-colors duration-300 group-hover:bg-brand/15 group-hover:text-brand-hover">
                <s.icon size={22} strokeWidth={1.8} />
              </div>
              <h3 className="mt-6 font-display text-xl md:text-2xl font-bold tracking-tight text-navy">
                {s.title}
              </h3>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-body">{s.copy}</p>
              <ul className="mt-5 space-y-1.5">
                {s.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-[13px] font-medium text-navy/65">
                    <span className="h-1 w-1 rounded-full bg-brand" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
